<div class="modal-header" *transloco="let t; prefix: 'parts.modals'">
  <h5 class="modal-title">{{ modalTitle }}</h5>
  <button
    type="button"
    class="btn-close"
    [attr.aria-label]="'common.close' | transloco"
    (click)="onCancel()">
    <i class="ph ph-x"></i>
  </button>
</div>

<div class="modal-body" *transloco="let t; prefix: 'parts.modals'">
  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <div class="form-group">
      <label>{{ t('fields.partName') }} <span class="required">*</span></label>
      <input
        class="form-control"
        type="text"
        formControlName="partName"
        [placeholder]="isPartLoading ? t('placeholders.loadingPartName') : ''"
        [disabled]="true" />
    </div>

    <div class="form-group">
      <label>{{ t('fields.partNumber') }} <span class="required">*</span></label>
      <input
        class="form-control"
        type="text"
        formControlName="partNumber"
        [placeholder]="isPartLoading ? t('placeholders.loadingPartNumber') : ''"
        [disabled]="true" />
    </div>

    <div class="form-group">
      <label>{{ t('fields.description') }}</label>
      <textarea
        class="form-control textarea"
        formControlName="description"
        rows="2"
        [placeholder]="isPartLoading ? t('placeholders.loadingDescription') : ''"
        [disabled]="true"></textarea>
    </div>

    <div class="form-group">
      <app-flatpickr-date-picker
        [label]="t('fields.dateReceived')"
        [required]="true"
        [value]="form.get('date')?.value"
        [dateFormat]="'Y-m-d'"
        (valueChange)="onDateChange($event)">
      </app-flatpickr-date-picker>
    </div>

    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('fields.quantityReceived') }} <span class="required">*</span></label>
        <input
          class="form-control"
          type="number"
          min="0"
          formControlName="quantityReceived"
          [placeholder]="t('placeholders.enterQuantity')" />
      </div>

      <div class="form-group half-width">
        <label>{{ t('fields.unitCost') }} <span class="required">*</span></label>
        <div class="unit-cost-group">
          <span class="currency-prefix">{{ currencySymbol }}</span>
          <input
            class="form-control unit-cost-input"
            type="number"
            min="0"
            formControlName="unitCost"
            [placeholder]="t('placeholders.enterUnitCost')" />
        </div>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('fields.invoiceNumber') }}</label>
        <input
          class="form-control"
          type="text"
          formControlName="invoiceNumber"
          [placeholder]="t('placeholders.enterInvoiceNumber')" />
      </div>

      <div class="form-group half-width">
        <label>{{ t('fields.supplier') }} <span class="required">*</span></label>
        <div ngbDropdown class="custom-dropdown" [autoClose]="true">
          <button
            type="button"
            class="btn form-control dropdown-toggle"
            ngbDropdownToggle
            [disabled]="isSuppliersLoading || !supplierOptions.length">
            <div class="d-flex flex-row justify-content-between dropdown-inner">
              <span
                class="align-middle value-text"
                [class.has-value]="!!form.get('supplier')?.value"
                >{{ supplierLabel }}</span
              >
              <i class="ph ph-caret-down lh-base"></i>
            </div>
          </button>

          <div ngbDropdownMenu>
            <div class="d-flex flex-column">
              @if (isSuppliersLoading) {
                <div class="dropdown-loading">
                  <span
                    class="spinner-border spinner-border-sm dropdown-loading-spinner"></span>
                  <span>{{ t('dropdown.loadingSuppliers') }}</span>
                </div>
              } @else if (!supplierOptions.length) {
                <div class="dropdown-empty">{{ t('dropdown.noSuppliersFound') }}</div>
              } @else {
                @for (option of supplierOptions; track option.value) {
                  <button
                    type="button"
                    class="btn btn-outline"
                    ngbDropdownItem
                    (click)="onSelect('supplier', option.value, $event)">
                    {{ option.label }}
                  </button>
                }
              }
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="form-row">
      <div class="form-group half-width">
        <label>{{ t('fields.warranty') }}</label>
        <div class="split-dropdown-group">
          <div ngbDropdown class="custom-dropdown value-dropdown" [autoClose]="true">
            <div class="dropdown-input-wrapper">
              <input
                type="number"
                min="0"
                class="form-control dropdown-toggle"
                formControlName="warrantyValue"
                ngbDropdownToggle
                placeholder="2" />
              <i class="ph ph-caret-down lh-base"></i>
            </div>
            <div ngbDropdownMenu>
              <div class="d-flex flex-column">
                @for (option of warrantyValueOptions; track option.value) {
                  <button
                    type="button"
                    class="btn btn-outline"
                    ngbDropdownItem
                    (click)="onSelect('warrantyValue', option.value, $event)">
                    {{ option.label }}
                  </button>
                }
              </div>
            </div>
          </div>

          <div ngbDropdown class="custom-dropdown unit-dropdown" [autoClose]="true">
            <button
              type="button"
              class="btn form-control dropdown-toggle"
              ngbDropdownToggle>
              <div class="d-flex flex-row justify-content-between dropdown-inner">
                <span
                  class="align-middle value-text"
                  [class.has-value]="!!form.get('warrantyUnit')?.value"
                  >{{ warrantyUnitLabel }}</span
                >
                <i class="ph ph-caret-down lh-base"></i>
              </div>
            </button>
            <div ngbDropdownMenu>
              <div class="d-flex flex-column">
                @for (option of warrantyUnitOptions; track option.value) {
                  <button
                    type="button"
                    class="btn btn-outline"
                    ngbDropdownItem
                    (click)="onSelect('warrantyUnit', option.value, $event)">
                    {{ option.label }}
                  </button>
                }
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="form-group half-width">
        <label>{{ t('fields.leadTime') }} <i
            class="ph ph-info lead-time-info"
            [ngbPopover]="t('addStock.leadTimeInfo')"
            triggers="mouseenter:mouseleave"
            placement="auto"
            popoverClass="warehouse-name-popover"
            container="body"></i></label>
        <input
          class="form-control"
          type="number"
          min="0"
          formControlName="leadTime"
          [placeholder]="t('placeholders.enterLeadTime')" />
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="cancel-btn" (click)="onCancel()">
        {{ 'common.cancel' | transloco }}
      </button>
      <button
        type="submit"
        class="save-btn"
        [disabled]="
          isLoading || isPartLoading || isSuppliersLoading || form.invalid
        ">
        @if (isLoading) {
          <span class="spinner-border"></span>
        } @else {
          <span>{{ t('actions.save') }}</span>
        }
      </button>
    </div>
  </form>
</div>
